import React, { useEffect, useMemo, useState } from "react";
import {
	FiEye,
	FiEyeOff,
	FiAlertTriangle,
	FiChevronRight,
	FiUser,
	FiBell,
	FiShoppingCart,
	FiMapPin,
	FiHeadphones,
	FiSettings,
	FiLogOut,
	FiShoppingBag,
	FiTruck,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const SectionTitle = ({ children }) => (
	<p className="px-4 pt-6 text-xs font-extrabold uppercase tracking-wide text-gray-600">
		{children}
	</p>
);

const Card = ({ children, className = "" }) => (
	<div className={`rounded-2xl bg-white p-4 shadow-sm ${className}`}>{children}</div>
);

const MenuRow = ({ icon: Icon, label, isLast, onClick }) => (
	<button
		type="button"
		className={`flex w-full items-center gap-3 px-4 py-4 text-left ${isLast ? "" : "border-b border-gray-100"}`}
		onClick={onClick}
	>
		<span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#19C463]">
			<Icon className="h-5 w-5" />
		</span>
		<span className="flex-1 text-sm font-semibold text-gray-900">{label}</span>
		<FiChevronRight className="h-4 w-4 text-gray-400" />
	</button>
);

const ProfileScreen = () => {
	const [showEscrow, setShowEscrow] = useState(false);
	const [buyerName, setBuyerName] = useState("User");
	const [buyerEmail, setBuyerEmail] = useState("");
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [totalOrdersAmount, setTotalOrdersAmount] = useState(0);
	const navigate = useNavigate();
	useEffect(() => {
		const fetchProfile = async () => {
			try {
				const token = localStorage.getItem("userToken");
				if (!token) {
					setIsLoggedIn(false);
					setBuyerEmail("");
					setTotalOrdersAmount(0);
					return;
				}
				setIsLoggedIn(true);
				const response = await fetch(
					`${process.env.REACT_APP_API_URL}/api/buyers/me`,
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);
				const result = await response.json();
				if (!response.ok || !result?.success) {
					setBuyerEmail("");
					setTotalOrdersAmount(0);
					return;
				}
				const profile = result?.data || {};
				if (profile.fullName) setBuyerName(profile.fullName);
				if (profile.email) setBuyerEmail(profile.email);
				setTotalOrdersAmount(Number(profile.totalOrdersAmount) || 0);
			} catch (error) {
				setIsLoggedIn(false);
				setBuyerEmail("");
				setTotalOrdersAmount(0);
				// keep defaults if fetch fails
			}
		};
		fetchProfile();
	}, []);

	const formattedEscrow = useMemo(() => {
		if (showEscrow) {
			return `₦${totalOrdersAmount.toLocaleString("en-NG")}.00`;
		}
		return "****.00";
	}, [showEscrow, totalOrdersAmount]);

	const accountRows = [
		{ label: "Account Information", icon: FiUser },
		{ label: "Notifications", icon: FiBell },
		{ label: "Orders", icon: FiShoppingCart, onClick: () => navigate("/orders") },
		{ label: "Address", icon: FiMapPin },
		{ label: "Customer Support", icon: FiHeadphones, onClick: () => navigate("/customer-care") },
		{ label: "Settings", icon: FiSettings },
		{ label: "Log out", icon: FiLogOut },
	];

	const forceRows = [
		{
			label: "Sell on BUY iT",
			icon: FiShoppingBag,
			onClick: () => {
				// Log out user
				localStorage.removeItem("userToken");
				// Optionally clear other session data
				// Redirect to vendor-login
				navigate("/vendor-login");
			},
		},
		{ label: "Become a delivery rider on BUY iT", icon: FiTruck, onClick: () => navigate("/logistic-rider-login") },
	];

	return (
		<div className="min-h-screen bg-[#F1F3F2] text-gray-900">
			<header className="bg-[#19C463] px-4 py-8 text-white">
				<p className="text-lg font-semibold">
					Welcome, <span className="font-extrabold">{isLoggedIn ? buyerName : "User"}</span> 🙂
				</p>
				{isLoggedIn ? (
					<p className="mt-2 text-sm">
						<span className="text-white/90">{buyerEmail}</span>
					</p>
				) : (
					<p className="mt-2 text-sm text-white/90">
						No Email Yet. Login to view Products and get a free delivery on your first order.{" "}
						<button
							type="button"
							onClick={() => navigate("/login")}
							className="font-semibold text-white underline underline-offset-2"
						>
							Log In
						</button>
					</p>
				)}
			</header>

			<div className="px-4 -mt-6">
				<Card className="flex items-center justify-between">
					<div>
						<p className="text-sm font-semibold text-gray-600">Escrow Balance</p>
						<p className="mt-2 text-lg font-extrabold text-gray-900">
							{formattedEscrow}
						</p>
					</div>
					<button
						type="button"
						onClick={() => setShowEscrow((prev) => !prev)}
						className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-[#19C463]"
						aria-label={showEscrow ? "Hide balance" : "Show balance"}
					>
						{showEscrow ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
					</button>
				</Card>

				<Card className="mt-4 flex items-start gap-3">
					<FiAlertTriangle className="mt-0.5 h-5 w-5 text-[#19C463]" />
					<p className="text-sm text-gray-700">
						Your payment is held here until you confirm that your order has been delivered.
					</p>
				</Card>
			</div>

			<SectionTitle>My BUY iT account</SectionTitle>
			<div className="px-4">
				<Card className="p-0">
					{accountRows.map((row, index) => (
						<MenuRow
							key={row.label}
							icon={row.icon}
							label={row.label}
							isLast={index === accountRows.length - 1}
							onClick={row.onClick}
						/>
					))}
				</Card>
			</div>

			<SectionTitle>BUY iT Force</SectionTitle>
			<div className="px-4 pb-8">
				<Card className="p-0">
					{forceRows.map((row, index) => (
						<MenuRow
							key={row.label}
							icon={row.icon}
							label={row.label}
							isLast={index === forceRows.length - 1}
							onClick={row.onClick}
						/>
					))}
				</Card>
			</div>
		</div>
	);
};

export default ProfileScreen;

